import React, { useEffect, useRef, useState } from "react";
import Cardacademy from "../../components/CategoryCard.js";
import estudiantesService from "../../services/estudiantesService.js";
import gradesService from "../../services/gradesService.js";
import Chart from "chart.js/auto";
import userProfile from "../../img/user-profile.png";
import moment from "moment";
import 'moment/locale/es'; // Importa el locale en español
import AnunciosDashboard from "../Comunicacion/anunciosdashboard.js";

moment.locale('es'); // Configura moment para usar el idioma español

const Dashboard = () => {
  const chartRef = useRef(null); // Ref para el canvas del gráfico
  const chartInstance = useRef(null); // Ref para la instancia del gráfico (myChart)
  const [gradesData, setGradesData] = useState([]); // Datos de estudiantes por grado
  const [birthdayStudents, setBirthdayStudents] = useState([]); // Datos de estudiantes que cumplen años

  // Función para obtener datos de grados y estudiantes
  const fetchGradesData = async () => {
    try {
      const grades = await gradesService.getGradesWithStudentAmount();
      const data = grades.map(grade => ({
        grade: grade.name,
        count: grade.studentAmount,
      }));
      setGradesData(data);
    } catch (error) {
      console.error("Error fetching grades data:", error);
    }
  };

  // Función para obtener datos de estudiantes que cumplen años
  const fetchBirthDayStudents = async () => {
    try {
      const result = await estudiantesService.getEstudiantesCumpleanios();
      if (result && result.length > 0) {
        setBirthdayStudents(result);
      } else {
        setBirthdayStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students data:", error);
    }
  };

  // useEffect para cargar datos de estudiantes por grado y cumpleaños
  useEffect(() => {
    fetchGradesData();
    fetchBirthDayStudents();
  }, []);

  // useEffect para inicializar o actualizar el gráfico
  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy(); // Destruye la instancia previa del gráfico si existe
    }

    const ctx = chartRef.current.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "#DCF2F1");
    gradient.addColorStop(0.33, "#7FC7D9");
    gradient.addColorStop(0.66, "#365486");
    gradient.addColorStop(1, "#0F1035");

    // Crear una nueva instancia del gráfico
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: gradesData.map((data) => data.grade),
        datasets: [
          {
            label: "Estudiantes por Grado",
            backgroundColor: gradient,
            data: gradesData.map((data) => data.count),
          },
        ],
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: '#f1f1f1' // Color del grid de fondo
            }
          },
          x: {
            grid: {
              display: false // Ocultar el grid en el eje X
            }
          }
        },
        plugins: {
          legend: {
            display: false // Ocultar leyenda del gráfico
          }
        }
      },
    });

    // Cleanup para destruir la instancia del gráfico cuando el componente se desmonte
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [gradesData]);

  // Función para mostrar la insignia según el cumpleaños del estudiante
  const getBadge = (date) => {
    const today = moment();
    const birthDate = moment(date, 'DD [de] MMMM [de] YYYY'); // Formato en español

    if (birthDate.isSame(today, 'day')) {
      return <div className="badge badge-subtle-danger sm">🎂 Hoy</div>;
    } else if (birthDate.isBefore(today, 'day')) {
      // Ya cumplió años este mes
      return <div className="badge badge-subtle-secondary">🎉 HBD</div>;
    } else if (birthDate.isSame(today.clone().add(1, 'days'), 'day')) {
      return <div className="badge badge-subtle-warning">Mañana</div>;
    }
    return null;
  };

  // 0 = cumple hoy (primero), 1 = próximo a cumplir, 2 = ya cumplió (al final)
  const getBirthdayRank = (date) => {
    const today = moment();
    const birthDate = moment(date, 'DD [de] MMMM [de] YYYY');
    if (birthDate.isSame(today, 'day')) return 0;
    if (birthDate.isAfter(today, 'day')) return 1;
    return 2;
  };

  // Hoy primero, luego los próximos (del más cercano al más lejano), y al
  // final los que ya cumplieron (del más reciente al más antiguo).
  const sortBirthdays = (a, b) => {
    const rankA = getBirthdayRank(a.currentBirthDate);
    const rankB = getBirthdayRank(b.currentBirthDate);
    if (rankA !== rankB) return rankA - rankB;

    const dateA = moment(a.currentBirthDate, 'DD [de] MMMM [de] YYYY');
    const dateB = moment(b.currentBirthDate, 'DD [de] MMMM [de] YYYY');
    return rankA === 2 ? dateB.diff(dateA) : dateA.diff(dateB);
  };

  return (
    <div className="container-fluid p-3">
      <h1 className="mb-4">Dashboard</h1>

      {/* Tarjetas de información */}
      <div className="row mb-4 g-4">
        <Cardacademy
          title="Descuentos"
          icon="fas fa-percent"
          link="/cartera/descuentos"
          className="teachers-card"
        />
        <Cardacademy
          title="Recibos de pagos"
          icon="fas fa-receipt"
          link="/cartera/facturacion"
          className="calendar-card"
        />
        <Cardacademy
          title="Estado de Cuentas"
          icon="fa-solid fa-circle-dollar-to-slot"
          link="/cartera/estadodecuentas"
          className="students-card"
        />
        <Cardacademy
          title="Matrículas"
          icon="fas fa-address-book"
          link="/matriculas"
          className="grades-card"
        />
        <Cardacademy
          title="Conceptos de pago"
          icon="fas fa-cash-register"
          link="/cartera/conceptos"
          className="subjects-card"
        />
      </div>

      {/* Gráfico de estudiantes por grado */}
      <div className="row mb-4">
        <div className="col-12 col-lg-8 d-flex">
          <div className="card flex-fill shadow-sm">
            <div className="card-header bg-gradient">
              <h5 className="card-title">Estudiantes por Grado</h5>
              <h6 className="card-subtitle text-muted">
                Muestra la cantidad de estudiantes matriculados por cada grado.
              </h6>
            </div>
            <div className="card-body">
              <div className="chart-container" style={{ height: '340px' }}>
                <canvas id="chartjs-bar" ref={chartRef} />
              </div>
            </div>
          </div>
        </div>

        {/* Lista de cumpleaños de estudiantes */}
        <div className="col-12 col-lg-4 d-flex">
          <div className="card flex-fill shadow-sm">
            <div className="card-header">
              <span className="badge bg-info float-end text-capitalize">{moment().format('MMMM')}</span>
              <h5 className="card-title mb-0">Cumpleaños de estudiantes</h5>
            </div>
            <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {birthdayStudents.length === 0 && (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-birthday-cake fa-2x mb-2 d-block"></i>
                  No hay cumpleaños este mes.
                </div>
              )}
              {[...birthdayStudents]
                .sort(sortBirthdays)
                .map((student, index) => {
                  const isToday = moment(student.currentBirthDate, 'DD [de] MMMM [de] YYYY').isSame(moment(), 'day');
                  return (
                    <div
                      key={index}
                      className={`d-flex align-items-center p-2 rounded-3 mb-2 ${isToday ? 'bg-primary bg-opacity-10' : ''}`}
                    >
                      <img
                        src={(student && student.urlImage) ? `${student.urlImage}` : userProfile}
                        width="48"
                        height="48"
                        className="rounded-circle me-3"
                        alt={student.fullName}
                      />
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{student.fullName}</h6>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <span className="badge badge-subtle-primary">{student.grade}</span>
                          <small className="text-muted">{student.currentBirthDate}</small>
                          {getBadge(student.currentBirthDate)}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default Dashboard;
